<template>
  <div id="manage-trackers-wrapper">
    <header class="px-5 py-4 m-0 bg-white d-flex align-items-center justify-content-center border-bottom">
      <h5 class="m-2">
        Manage Trackers
      </h5>
      <div class="filters d-flex">
        <h6>Filters</h6>
        <b-form-select
          v-model="filters.creative"
          :options="creativeOptions"
          size="sm"
          data-test="creative-filter-select"
        />
        <b-form-select
          v-model="filters.publisher"
          :options="publisherOptions"
          size="sm"
          data-test="publisher-filter-select"
        />
        <b-form-select
          v-model="filters.country"
          :options="countryOptions"
          size="sm"
          data-test="country-filter-select"
        />
        <b-form-select
          v-model="filters.geo"
          :options="geoOptions"
          size="sm"
          data-test="geo-filter-select"
        />
      </div>
      <div class="actions">
        <b-button
          class="mr-3"
          variant="secondary"
          @click="headerActions().reValidate()"
          :disabled="isCampaignReadonly"
        >
          Re-validate trackers
        </b-button>
        <b-button
          variant="primary"
          @click="headerActions().getTrackersCSV()"
        >
          Download as CSV
        </b-button>
      </div>
    </header>

    <div
      v-if="isBusy"
      class="container align-items-center d-flex justify-content-center card-body mx-auto"
    >
      <div class="loader mx-auto" />
    </div>
    <div
      v-else-if="!hasTrackersGroups"
      class="container"
    >
      <div class="row h-100 justify-content-center align-items-center">
        <div class="col-12 text-center">
          <p>
            This campaign doesn't have any trackers configured
          </p>
          <b-button
            data-test="upload-trackers"
            variant="lg btn-secondary"
            @click="openTrackersModal"
            :disabled="isCampaignReadonly"
          >
            Upload trackers
          </b-button>
        </div>
      </div>
    </div>
    <div
      v-else
      class="table-responsive manage-trackers-table-wrapper"
    >
      <b-table
        ref="trackers"
        class="card-table table-vcenter text-nowrap"
        tbody-tr-class="trackers-table-row"
        :striped="false"
        :bordered="false"
        :outlined="false"
        :hover="true"
        :items="Object.values(filteredTrackerGroupedList)"
        :key="forceRenderCount"
        :fields="getHeaders"
        :busy.sync="isBusy"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        show-empty
        data-test="trackers-list"
      >
        <template v-slot:table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="`width: ${field.width ? field.width : 'auto'}`"
          >
        </template>
        <template v-slot:cell(creativeId)="data">
          <p
            class="mb-0"
          >
            {{ getCreativeName(data.item[0].creativeId) }}
          </p>
          <p
            v-if="isGroupTrackersPlacementLevel(data.item)"
            class="tag bg-blue text-white small"
          >
            PLACEMENT LEVEL
          </p>
        </template>
        <template v-slot:cell(publisherCode)="data">
          {{ getPublisherName(data.item[0].publisherCode) }}
        </template>
        <template v-slot:cell(countryCode)="data">
          {{ getGeoName(data.item[0], 'countryCode') }}
        </template>
        <template v-slot:cell(geoCodes)="data">
          {{ getGeoName(data.item[0], 'geoCodes') }}
        </template>
        <template v-slot:cell(impressionTracker)="data">
          <status-btn
            v-if="getImpressionTrackerInfo(data.item).length"
            tracker-type="impressionTracker"
            :scope="getGroupedTrackerScope(data.item)"
            :trackers-info="getImpressionTrackerInfo(data.item)"
            :is-group="true"
            :border="true"
          />
        </template>
        <template v-slot:cell(clickTracker)="data">
          <status-btn
            v-if="getClickTrackerInfo(data.item).length"
            tracker-type="clickTracker"
            :scope="getGroupedTrackerScope(data.item)"
            :trackers-info="getClickTrackerInfo(data.item)"
            :is-group="true"
            :border="true"
          />
        </template>
        <template v-slot:cell(postClickTracker)="data">
          <status-btn
            v-if="getPostClickTrackerInfo(data.item).length"
            tracker-type="postClickTracker"
            :scope="getGroupedTrackerScope(data.item)"
            :trackers-info="getPostClickTrackerInfo(data.item)"
            :is-group="true"
            :border="true"
          />
        </template>
        <template v-slot:cell(thirdPartyTags)="data">
          <status-btn
            v-if="getThirdPartyTagsInfo(data.item).length"
            tracker-type="thirdPartyTags"
            :scope="getGroupedTrackerScope(data.item)"
            :trackers-info="getThirdPartyTagsInfo(data.item)"
            :display-count="true"
            :is-group="true"
            :border="true"
            :disabled="!isGroupTrackersPlacementLevel(data.item)"
          />
        </template>
        <template v-slot:cell(actions)="data">
          <b-button
            size="lg"
            variant="secondary"
            @click="rowActions().editTrackers(data.item)"
            :disabled="isCampaignReadonly"
          >
            Edit
          </b-button>
          <b-button
            class="btn-danger ml-3"
            size="lg"
            variant="transparant"
            @click="rowActions().deleteTrackers(data)"
            :disabled="isCampaignReadonly || groupToDelete.includes(data.item[0].group)"
          >
            <i class="fa-light fa-trash-can" />
          </b-button>
        </template>
        <template v-slot:empty>
          <p>
            This campaign doesn't have any trackers configured
          </p>
        </template>
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <div class="loader mx-auto" />
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
      <div class="upload-btn d-flex justify-content-center w-100 my-5">
        <b-button
          data-test="upload-trackers"
          size="lg"
          variant="secondary"
          @click="openTrackersModal"
          :disabled="isCampaignReadonly"
        >
          Upload trackers
        </b-button>
      </div>
    </div>
    <base-wizard-footer
      id="tracker-footer"
      :z-index="200"
      :next-text="'Done'"
      :show-back-button="true"
      :next-icon="false"
      @previous-step="onPreviousStep"
    />
    <tracker-modal
      id="trackersModal"
      ref="trackersModalRef"
    >
      <tracker-wizard @closed="getTrackers" />
    </tracker-modal>
    <tracker-modal
      id="editContentTrackersModal"
      ref="editContentTrackersModalRef"
    >
      <edit-content-trackers-modal
        :tracker-combination="selectedCombination"
        :tracker-items="selectedTrackerGroup"
      />
    </tracker-modal>
    <tracker-modal
      id="editPlacementTrackersModal"
      ref="editPlacementTrackersModalRef"
    >
      <edit-placement-trackers-modal
        :tracker-combination="selectedCombination"
        :item="selectedTrackerGroup"
      />
    </tracker-modal>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';

import * as trackersTypes from '@/modules/trackers/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as rootTypes from '@/store/types';
import * as contentTypes from '@/modules/content/store/types';

import fileHelper from '@/core/mixins/fileHelper';
import urlHelper from '@/core/mixins/urlHelper';
import trackerHelper from '@/core/mixins/trackerHelper';
import confirmBoxHelper from '@/core/mixins/confirmBoxHelper';

import EditContentTrackersModal from '@/modules/trackers/components/editTrackers/EditContentTrackers.vue';
import TrackerWizard from '@/modules/trackers/components/TrackerWizard.vue';
import TrackerModal from '@/modules/trackers/components/TrackerModal.vue';
import StatusBtn from '@/modules/trackers/components/trackerHealth/StatusBtn.vue';
import tableHeaders from '@/views/Campaign/tableHeaders';
import BaseWizardFooter from '@/components/BaseWizardFooter.vue';
import EditPlacementTrackersModal from '@/modules/trackers/components/editTrackers/EditPlacementTrackers.vue';

export default {
  name: 'ManageTrackers',
  components: {
    EditPlacementTrackersModal,
    BaseWizardFooter,
    TrackerModal,
    TrackerWizard,
    EditContentTrackersModal,
    StatusBtn,
  },
  mixins: [
    fileHelper,
    urlHelper,
    trackerHelper,
    confirmBoxHelper],
  data() {
    return {
      ...tableHeaders,
      trackerGroupedList: {},
      selectedCombination: null,
      selectedTrackerGroup: [],
      sortBy: '',
      sortDesc: false,
      isBusy: false,
      filters: {
        creative: null,
        publisher: null,
        country: null,
        geo: null,
      },
      trackersPolling: null,
      forceRenderCount: 0,
      groupToDelete: [],
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'regionsMap',
      'countries',
    ]),
    ...mapState('content', [
      'creatives',
      'contentItems',
      'feedId',
    ]),
    ...mapState('trackers', [
      'groupedTrackersList',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    hasTrackersGroups() {
      if (!this.groupedTrackersList) return false;
      return Object.keys(this.groupedTrackersList).length !== 0;
    },
    filteredTrackerGroupedList() {
      const returnList = {};
      // eslint-disable-next-line no-unused-vars
      const count = this.forceRenderCount;

      Object.entries(this.groupedTrackersList).forEach((value) => {
        const key = value[0];
        const items = value[1];
        const filteredItems = items.filter((trackerItem) => {
          const {
            creative, publisher, country, geo,
          } = this.filters;
          const hasCreativeId = creative ? trackerItem.creativeId === creative : true;
          const hasPublisherId = publisher ? trackerItem.publisherId === publisher : true;
          const hasCountryId = country ? trackerItem.countryIds.some((countryId) => countryId === country) : true;
          const hasGeoGroupId = geo ? trackerItem.geoGroupIds.some((geoId) => geoId === geo) : true;
          return hasCreativeId && hasPublisherId && hasCountryId && hasGeoGroupId;
        });
        if (filteredItems.length > 0) {
          returnList[key] = filteredItems;
        }
      });
      return returnList;
    },
    creativeOptions() {
      const seen = {};
      if (!this.groupedTrackersList) return;
      // eslint-disable-next-line no-return-assign
      const options = Object.values(this.groupedTrackersList).flat().filter((item) => (seen.hasOwnProperty(item.creativeId) ? false : (seen[item.creativeId] = true)));
      const defaultOption = { text: 'All Creatives', value: null };
      return [defaultOption, ...options.map((item) => ({
        text: this.getCreativeName(item.creativeId),
        value: item.creativeId,
      }))];
    },
    publisherOptions() {
      const seen = {};
      if (!this.groupedTrackersList) return;
      // eslint-disable-next-line no-return-assign
      const options = Object.values(this.groupedTrackersList).flat().filter((item) => (seen.hasOwnProperty(item.publisherId) || item.publisherId === null ? false : (seen[item.publisherId] = true)));
      const defaultOption = { text: 'All Publishers', value: null };
      return [defaultOption, ...options.map((item) => ({
        text: this.getPublisherName(item.publisherCode),
        value: item.publisherId,
      }))];
    },
    geoOptions() {
      const seen = {};
      const options = [];
      if (!this.groupedTrackersList) return;
      Object.values(this.groupedTrackersList).flat().forEach((item) => {
        // eslint-disable-next-line no-return-assign
        options.push(...item.geoGroupIds.filter((geoId) => (seen.hasOwnProperty(geoId) ? false : (seen[geoId] = true))));
      });
      const defaultOption = { text: 'All Geo Groups', value: null };
      return [defaultOption, ...options.map((geoId) => ({
        text: this.getGeoGroupNameById(geoId),
        value: geoId,
      }))];
    },
    countryOptions() {
      const seen = {};
      const options = [];
      if (!this.groupedTrackersList) return;
      Object.values(this.groupedTrackersList).flat().forEach((item) => {
        // eslint-disable-next-line no-return-assign
        options.push(...item.countryIds.filter((countryId) => (seen.hasOwnProperty(countryId) ? false : (seen[countryId] = true))));
      });
      const defaultOption = { text: 'All Countries', value: null };
      return [defaultOption, ...options.map((countryId) => ({
        text: this.getCountryNameById(countryId),
        value: countryId,
      }))];
    },
    getHeaders() {
      return [
        ...this.manageTrackersListHeaders,
      ];
    },
  },
  methods: {
    ...mapMutations({
      setGlobalMessage: rootTypes.SET_GLOBAL_MESSAGE,
    }),
    ...mapActions('trackers', {
      deleteTrackers: trackersTypes.DELETE_GROUP_FROM_GROUPED_LIST,
      downloadTrackersCSV: trackersTypes.GET_TRACKERS_CSV,
      reValidateTrackers: trackersTypes.PUT_TRACKERS_REVALIDATION,
      convertTrackersResponse: trackersTypes.ADD_GROUPED_TRACKERS_LIST,
    }),
    ...mapActions('content', [
      contentTypes.FETCH_FILTERED_FEED_CONTENT_ITEMS,
    ]),
    setAlertMessage({ message, type }) {
      if (!type || !message) throw new Error('type or message prop not provided');
      this.setGlobalMessage({ message, type });
    },
    filteredPendingTracker() {
      return Object.values(this.groupedTrackersList).flat().filter((trackerItem) => {
        const {
          clickTracker, impressionTracker, postClickTracker, imageTrackers, scriptTrackers,
        } = trackerItem;
        const pending = 'PENDING';
        const hasClickTrackerPending = clickTracker ? clickTracker.status === pending : false;
        const hasImpressionTrackerPending = impressionTracker ? impressionTracker.status === pending : false;
        const hasPostClickTrackerPending = postClickTracker ? postClickTracker.status === pending : false;
        const hasImageTrackersPending = imageTrackers ? imageTrackers.some((item) => item.status === pending) : false;
        const hasScriptTrackersPending = scriptTrackers ? scriptTrackers.some((item) => item.status === pending) : false;
        return hasClickTrackerPending || hasImpressionTrackerPending || hasPostClickTrackerPending || hasImageTrackersPending || hasScriptTrackersPending;
      });
    },
    headerActions() {
      const scope = this;
      async function getTrackersCSV() {
        const campaignId = scope?.campaign?.id;
        if (!campaignId) return;
        const csvContent = await scope.downloadTrackersCSV(campaignId);
        return scope.downloadCsvFile(csvContent, `campaign-trackers-${campaignId}`);
      }
      async function reValidate() {
        const campaignId = scope?.campaign?.id;
        await scope.reValidateTrackers(campaignId);
        await scope.getTrackers(false);
        scope.setAlertMessage({ message: 're-validating trackers', type: 'warning' });
        scope.trackersPolling = setInterval(async () => {
          if (scope.filteredPendingTracker().length === 0) {
            clearInterval(scope.trackersPolling);
            scope.setAlertMessage({ message: 're-validation completed', type: 'success' });
          } else await scope.getTrackers(false);
        }, 1000);
      }
      return {
        getTrackersCSV,
        reValidate,
      };
    },
    rowActions() {
      const scope = this;

      function editTrackers(trackersGroups) {
        const { group } = trackersGroups[0];
        if (!group) return;
        scope.selectedCombination = group;
        if (scope.isGroupTrackersPlacementLevel(trackersGroups)) {
          scope.selectedTrackerGroup = trackersGroups[0];
          scope.openEditPlacementTrackersModal();
        } else {
          scope.selectedTrackerGroup = scope.trackerGroupedList[group];
          scope.openEditContentTrackersModal();
        }
      }

      async function deleteTrackers(trackerRow) {
        const confirmation = await scope.openDangerousActionConfirmBox('Are you sure you want to delete all grouped trackers for this row');
        if (!confirmation) return;
        scope.groupToDelete.push(trackerRow.item[0].group);
        const { group } = trackerRow.item[0];

        await scope.deleteTrackers(group);
        const groupToDeleteIndex = scope.groupToDelete.findIndex((groupName) => groupName === trackerRow.item[0].group);
        scope.groupToDelete.splice(groupToDeleteIndex, 1);
        scope.forceRenderCount += 1;
      }

      return {
        editTrackers,
        deleteTrackers,
      };
    },
    getGroupOfObjectKeys(array, key) {
      return array.flatMap((item) => item[key]);
    },
    getContentItemFromUuid(uuid) {
      return this.contentItems.find((cItem) => cItem.encodedExternalId === uuid);
    },
    getGroupedTrackerScope(trackersGroup) {
      if (!trackersGroup) return;
      return this.isGroupTrackersPlacementLevel(trackersGroup) ? 'FEED' : 'CONTENT_ITEM';
    },
    isGroupTrackersPlacementLevel(trackersGroup) {
      if (!trackersGroup) return;
      const scopes = this.getGroupOfObjectKeys(trackersGroup, 'scope');
      return scopes.some((scope) => scope === 'FEED');
    },
    getImpressionTrackerInfo(trackersGroup) {
      if (!trackersGroup) return [];
      const impressionTrackers = this.getGroupOfObjectKeys(trackersGroup, 'impressionTracker');
      const contentItemUuids = this.getGroupOfObjectKeys(trackersGroup, 'contentItemUuid');
      for (let index = 0; index < impressionTrackers.length; index++) {
        if (!impressionTrackers[index]) continue;
        impressionTrackers[index].contentItem = this.getContentItemFromUuid(contentItemUuids[index]);
      }
      return impressionTrackers.filter((tracker) => tracker != null);
    },
    getClickTrackerInfo(trackersGroup) {
      if (!trackersGroup) return [];
      const clickTrackers = this.getGroupOfObjectKeys(trackersGroup, 'clickTracker');
      const landingPages = this.getGroupOfObjectKeys(trackersGroup, 'landingPage');
      const contentItemUuids = this.getGroupOfObjectKeys(trackersGroup, 'contentItemUuid');
      for (let index = 0; index < clickTrackers.length; index++) {
        if (!clickTrackers[index]) continue;
        clickTrackers[index].contentItem = this.getContentItemFromUuid(contentItemUuids[index]);
        clickTrackers[index].landingPage = this.removeUrlTrailingSlash(landingPages[index]);
      }
      return clickTrackers.filter((tracker) => tracker != null);
    },
    getPostClickTrackerInfo(trackersGroup) {
      if (!trackersGroup) return [];
      const postClickTrackers = this.getGroupOfObjectKeys(trackersGroup, 'postClickTracker');
      const contentItemUuids = this.getGroupOfObjectKeys(trackersGroup, 'contentItemUuid');
      for (let index = 0; index < postClickTrackers.length; index++) {
        if (!postClickTrackers[index]) continue;
        postClickTrackers[index].contentItem = this.getContentItemFromUuid(contentItemUuids[index]);
      }
      return postClickTrackers.filter((tracker) => tracker != null);
    },
    getThirdPartyTagsInfo(trackersGroup) {
      if (!trackersGroup) return [];
      const scriptTrackers = this.getGroupOfObjectKeys(trackersGroup, 'scriptTrackers');
      const imageTrackers = this.getGroupOfObjectKeys(trackersGroup, 'imageTrackers');
      const contentItemUuids = this.getGroupOfObjectKeys(trackersGroup, 'contentItemUuid');
      for (let index = 0; index < scriptTrackers.length; index++) {
        if (!scriptTrackers[index]) continue;
        scriptTrackers[index].contentItem = this.getContentItemFromUuid(contentItemUuids[index]);
        scriptTrackers[index].type = 'scriptTrackers';
      }
      for (let index = 0; index < imageTrackers.length; index++) {
        if (!imageTrackers[index]) continue;
        imageTrackers[index].contentItem = this.getContentItemFromUuid(contentItemUuids[index]);
        imageTrackers[index].type = 'imageTrackers';
      }
      return scriptTrackers.concat(imageTrackers).filter((tracker) => tracker != null);
    },
    async getTrackers(setBusyStatus = true) {
      if (setBusyStatus) this.isBusy = true;
      const response = await this.convertTrackersResponse(this.campaign.id);
      if (response) {
        this.trackerGroupedList = response;
        if (setBusyStatus) this.isBusy = false;
      }
    },
    getCreativeName(id) {
      const creative = this.creatives.find((item) => (item.id === id));
      if (creative && creative.name) return creative.name;
      return '';
    },
    getPublisherName(code) {
      if (code) return code;
      return 'ALL';
    },
    getGeoGroupNameById(id) {
      if (!id) return;
      return this.regionsMap.get(id).name;
    },
    getCountryNameById(id) {
      if (!id) return;
      return this.countries[id];
    },
    getGeoName(data, type) {
      const validTypes = ['countryCode', 'geoCodes'];
      if (!validTypes.includes(type)) return;
      const { countryCode, geoCodes } = data;
      if (!countryCode && !geoCodes) return 'ALL';
      if (type === 'countryCode') {
        if (countryCode !== 'ALL' && geoCodes === 'ALL') return countryCode;
        return null;
      } if (type === 'geoCodes') {
        if (countryCode === 'ALL' && geoCodes !== 'ALL') return geoCodes;
        return null;
      }
      return null;
    },
    openTrackersModal() {
      this.$root.$emit('bv::show::modal', 'trackersModal');
    },
    openEditContentTrackersModal() {
      this.$root.$emit('bv::show::modal', 'editContentTrackersModal');
    },
    openEditPlacementTrackersModal() {
      this.$root.$emit('bv::show::modal', 'editPlacementTrackersModal');
    },
    onPreviousStep() {
      this.$emit('manageTrackersPreviousStep');
    },
  },
  async mounted() {
    await this.getTrackers();
    await this.FETCH_FILTERED_FEED_CONTENT_ITEMS({ feedId: this.feedId, allContent: true });
  },
};
</script>
<style lang="scss" scoped>
header  {
  h5 { min-width: 120px; width: 10%; }
  .filters {
    align-items: center;
    justify-content: space-between;
    width: 65%;
    padding: 0 1rem;
    h6 { width: 38px; margin: 0 1rem 0 0; }
    select { width: calc(25% - 10px - 1rem); }
  }
  .actions {
    width: 25%;
  }
}
</style>
<style lang="scss">
#manage-trackers-wrapper {
  height: 100%;
  overflow-y: auto;

  &>.container {
    height: calc(100% - 67px);
  }
}
#tracker-footer{
  position: fixed;
  bottom: 0;
  right: 0;
  width: calc(100% - 130px);
}
</style>
